import { createContext, useContext, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'e-ink';

interface ThemeContextValue {
  theme: Theme;
  toggle: () => void;
  setTheme: (t: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'light',
  toggle: () => {},
  setTheme: () => {},
});

const CYCLE: Theme[] = ['light', 'dark', 'e-ink'];

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = localStorage.getItem('mobiuspo-theme') as Theme | null;
    if (stored && CYCLE.includes(stored)) return stored;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark', 'e-ink');
    root.classList.add(theme);
    root.classList.add('theme-transition');
    localStorage.setItem('mobiuspo-theme', theme);
    const t = setTimeout(() => root.classList.remove('theme-transition'), 400);
    return () => clearTimeout(t);
  }, [theme]);

  const setTheme = (t: Theme) => setThemeState(t);
  const toggle = () =>
    setThemeState((prev) => CYCLE[(CYCLE.indexOf(prev) + 1) % CYCLE.length]);

  return (
    <ThemeContext.Provider value={{ theme, toggle, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
